import React, { useMemo, useState } from "react";
import { Row, Col, Button, Tag, Dropdown } from "antd";
import {
  MoreOutlined,
  EyeOutlined,
  FileTextOutlined,
  ClockCircleOutlined,
  // TransactionOutlined,
  SettingOutlined,
} from "@ant-design/icons";
import MetricsGrid from "@/components/UI/Layout/MetricGrid";
import StatsAtGlanceCard from "@/components/UI/Cards/standardCard";
import TransactionDetailsModal from "./modals/TransactionDetailsModal";
import { CustomDataTable } from "@/components/UI/Tables/Datatable";
// import { formatCurrency } from "@/utils/helper";
import { useTransactionData } from "./hooks/useTransactionData";
import { formatCurrency } from "@/utils/helper";

const TransactionManager = ({ tableTitle = "Transaction Logs" }) => {
  const [activeTab, setActiveTab] = useState(0);
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [openTransactionDetails, setOpenTransactionDetails] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);

  const handleTabChange = (newTab) => {
    // console.log("Tab changing from", activeTab, "to", newTab);
    setActiveTab(newTab);
    setSelectedRowKeys([]);
  };

  const {
    transactionsData,
    paginationInfo,
    transactionSummary,
    handlePageChange,
    handlePageSizeChange,
    search,
    handleSearch,
    isLoading: loading,
    isFetching,
  } = useTransactionData();

  /* --------------------------------------------------------------------- *
   *  1. Transform API → Table rows
   * --------------------------------------------------------------------- */
  const tableData = useMemo(() => {
    if (!transactionsData || transactionsData.length === 0) return [];

    return transactionsData.map((trx) => {
      const fullName = `${trx.first_name || ""} ${trx.last_name || ""}`.trim();

      return {
        key: trx.id.toString(),
        transactionId: trx.transaction_id, // ← matches column
        customer: fullName || "—", // ← matches column
        orderId: trx.order_reference, // ← matches column
        orderType: trx.order_type_name, // ← matches column
        amount: Number(trx.amount).toLocaleString(), // ← matches column
        paymentGateway: trx.platform, // ← you can add field later
        transactionDate: new Date(trx.created_at)
          .toLocaleString("en-GB", {
            day: "2-digit",
            month: "short",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
          })
          .replace(",", ""), // ← matches column
        status: trx.status, // ← matches column
        raw: trx, // keep whole object for modal
      };
    });
  }, [transactionsData]);

  /* --------------------------------------------------------------------- *
   *  2. Tab-filtered data
   * --------------------------------------------------------------------- */
  const bulkPurchase = tableData.filter((t) => t.orderType === "Bulk Purchase");
  const eventCatering = tableData.filter(
    (t) => t.orderType === "Event Catering"
  );

  const tableDataMap = {
    0: tableData,
    1: bulkPurchase,
    2: eventCatering,
  };

  /* --------------------------------------------------------------------- *
   *  3. Modal handlers
   * --------------------------------------------------------------------- */
  const openDetailsModal = (record) => {
    setSelectedTransaction(record.raw);
    setOpenTransactionDetails(true);
  };
  const closeDetailsModal = () => {
    setOpenTransactionDetails(false);
    setSelectedTransaction(null);
  };

  /* --------------------------------------------------------------------- *
   *  4. Metrics (you can compute real totals from API if you want)
   * --------------------------------------------------------------------- */
  // const totalAmount = tableData
  //   .reduce((sum, r) => sum + Number(r.amount.replace(/,/g, "")), 0)
  //   .toLocaleString(undefined, {
  //     minimumFractionDigits: 2,
  //     maximumFractionDigits: 2,
  //   });

  const metricData = useMemo(() => {
    return [
      {
        title: "Total Transactions",
        value: formatCurrency(transactionSummary.total_transaction),
        icon: <FileTextOutlined style={{ fontSize: "24px", color: "#fff" }} />,
        bgColor: "bg-[#E77800]",
        iconBg: "bg-[#F79223]",
        titleColor: "text-[#F3F4F6]",
        valueColor: "text-white",
      },
      {
        title: "Abandoned Settlements",
        value: transactionSummary.total_abandoned,
        icon: (
          <ClockCircleOutlined style={{ fontSize: "24px", color: "#F59E0B" }} />
        ),
      },
      {
        title: "Failed Payments",
        value: transactionSummary.total_failed,
        icon: (
          <FileTextOutlined style={{ fontSize: "24px", color: "#EF4444" }} />
        ),
      },
      {
        title: "Success Payments",
        value: transactionSummary.total_success,
        icon: (
          <FileTextOutlined style={{ fontSize: "24px", color: "#EF4444" }} />
        ),
      },
    ];
  }, [transactionSummary]);

  /* --------------------------------------------------------------------- *
   *  5. Status tag colours
   * --------------------------------------------------------------------- */
  const getStatusColor = (status) => {
    const map = {
      Pending: { color: "#F59E0B", bg: "#FEF3C7" },
      Approved: { color: "#10B981", bg: "#D1FAE5" },
      Completed: { color: "#10B981", bg: "#D1FAE5" },
      Failed: { color: "#EF4444", bg: "#FEE2E2" },
    };
    return map[status] || { color: "#6B7280", bg: "#F3F4F6" };
  };

  /* --------------------------------------------------------------------- *
   *  6. Action menu
   * --------------------------------------------------------------------- */
  const getActionMenuItems = (record) => [
    {
      key: "view",
      icon: <EyeOutlined />,
      label: "View Details",
      onClick: () => openDetailsModal(record),
    },
    // {
    //   key: "query",
    //   icon: <SettingOutlined />,
    //   label: "Query",
    //   onClick: () => console.log("Query transaction", record.transactionId),
    // },
  ];

  /* --------------------------------------------------------------------- *
   *  7. Table columns – **exact match** with transformed data
   * --------------------------------------------------------------------- */
  const transactionColumns = [
    {
      title: "Transaction Id",
      dataIndex: "transactionId",
      key: "transactionId",
      width: 160,
    },
    {
      title: "Customer",
      dataIndex: "customer",
      key: "customer",
      width: 160,
    },
    {
      title: "Order Id",
      dataIndex: "orderId",
      key: "orderId",
      width: 180,
    },
    {
      title: "Order Type",
      dataIndex: "orderType",
      key: "orderType",
      width: 120,
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      width: 120,
    },
    {
      title: "Payment Gateway",
      dataIndex: "paymentGateway",
      key: "paymentGateway",
      width: 120,
    },
    {
      title: "Date and Time",
      dataIndex: "transactionDate",
      key: "transactionDate",
      width: 180,
      align: "center",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 120,
      render: (status) => {
        const { color, bg } = getStatusColor(status);
        return (
          <Tag
            style={{
              color,
              backgroundColor: bg,
              border: `1px solid ${color}20`,
              borderRadius: "8px",
              padding: "2px 8px",
              fontSize: "12px",
              fontWeight: "500",
            }}
          >
            {status}
          </Tag>
        );
      },
    },
    {
      title: "Action",
      key: "action",
      width: 80,
      align: "center",
      render: (_, record) => (
        <Dropdown
          menu={{ items: getActionMenuItems(record) }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            className="text-gray-400 hover:text-gray-600"
          />
        </Dropdown>
      ),
    },
  ];

  /* --------------------------------------------------------------------- *
   *  8. Tabs (All / Bulk Purchase / Event Catering)
   * --------------------------------------------------------------------- */
  const transactionTabs = [
    { label: "All", count: paginationInfo.total },
    { label: "Bulk Purchase", count: bulkPurchase.length },
    { label: "Event Catering", count: eventCatering.length },
  ];

  /* --------------------------------------------------------------------- *
   *  Render
   * --------------------------------------------------------------------- */
  return (
    <div className="px-6 bg-gray-50 min-h-full">
      <MetricsGrid
        colSpan={{ xs: 24, sm: 12, lg: 6 }}
        CardComponent={StatsAtGlanceCard}
        data={metricData}
      />

      <Row>
        <Col span={24}>
          <CustomDataTable
            title={tableTitle}
            data={tableDataMap[activeTab] || tableData}
            columns={transactionColumns}
            tabs={transactionTabs}
            activeTab={activeTab}
            onTabChange={handleTabChange}
            loading={loading || isFetching}
            showSearch
            searchPlaceholder="Search transactions..."
            searchValue={search}
            onServerSearch={handleSearch}
            searchMode="server"
            showFilter={false}
            showCSV
            paginationMode="server"
            currentPage={paginationInfo.current_page}
            totalItems={paginationInfo.total}
            serverPageSize={paginationInfo.per_page}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            rowSelection={{
              selectedRowKeys,
              onChange: setSelectedRowKeys,
            }}
            selectedRowKeys={selectedRowKeys}
          />
        </Col>
      </Row>

      <TransactionDetailsModal
        open={openTransactionDetails}
        onClose={closeDetailsModal}
        transactionDetails={selectedTransaction}
      />
    </div>
  );
};

export default TransactionManager;
